import { Flex, IconButton } from '@aloft/toolkit-sui';

import React, { useCallback, useState } from 'react';

import { selectCompSequence } from '../../../redux/features/selectors/orderSelectors.ts';
import { selectFeatureGridExpandedPropertyId } from '../../../redux/features/selectors/uiSelectors.ts';
import { setFeatureGridExpandedPropertyId } from '../../../redux/features/uiSlice.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { useCraftsmanEditPropertyDetailsModal } from '../../CraftsmanEditPropertyDetailsModal/useCraftsmanEditPropertyDetailsModal.tsx';
import { useCompActions } from '../useCompActions.tsx';
import { DeleteConfirmationModal } from './DeleteConfirmationModal.tsx';
import { ValuationTablePropertyHeaderMenu } from './ValuationTablePropertyHeaderMenu.tsx';

type ValuationTablePropertyHeaderActionsProps = {
  propertyId: string;
  showDataSourceDrawerToggler: boolean;
  title: string;
};

export const ValuationTablePropertyHeaderActions = ({
  propertyId,
  showDataSourceDrawerToggler,
  title,
}: ValuationTablePropertyHeaderActionsProps) => {
  const dispatch = useAppDispatch();
  const compSequence = useAppSelector(selectCompSequence);
  const isSubject = propertyId === 'subject';

  const featureGridExpandedPropertyId = useAppSelector(selectFeatureGridExpandedPropertyId);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const closeDeleteModal = () => setIsDeleteModalOpen(false);
  const openDeleteConfirmationModal = () => setIsDeleteModalOpen(true);

  const EditPropertyModal = useCraftsmanEditPropertyDetailsModal(propertyId);

  const { removeComp, moveCompLeft, moveCompRight } = useCompActions(propertyId);

  const removeCompHandler = useCallback(() => {
    if (!isSubject) removeComp(propertyId);
  }, [isSubject, removeComp, propertyId]);

  const isComp = propertyId !== 'subject';
  const showLeftButton = isComp && propertyId !== compSequence[0];
  const showRightButton = isComp && propertyId !== compSequence[compSequence.length - 1];
  const propertyIsExpanded = featureGridExpandedPropertyId === propertyId;

  const onLeftButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    moveCompLeft();
  };

  const onRightButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    moveCompRight();
  };

  return (
    <Flex flexDirection="column" alignItems="center" justifyContent="space-between">
      <Flex flexDirection="column">
        <ValuationTablePropertyHeaderMenu
          openDeleteConfirmationModal={openDeleteConfirmationModal}
          openEditPropertyDetailsModal={EditPropertyModal.props.openModal}
          isRemovable={!isSubject}
        />
        {!isSubject && isDeleteModalOpen && (
          <DeleteConfirmationModal
            isDeleteModalOpen={isDeleteModalOpen}
            closeDeleteModal={closeDeleteModal}
            title={title}
            removeComp={removeCompHandler}
          />
        )}
        {EditPropertyModal.modal}
        {showLeftButton && (
          <IconButton
            ariaLabel="Move Left"
            size="xs"
            icon="arrow-left"
            variant="tertiary"
            iconColor="textSecondary"
            onClick={onLeftButtonClick}
          />
        )}
        {showRightButton && (
          <IconButton
            ariaLabel="Move Right"
            size="xs"
            icon="arrow-right"
            variant="tertiary"
            iconColor="textSecondary"
            onClick={onRightButtonClick}
          />
        )}
      </Flex>
      {showDataSourceDrawerToggler && (
        <IconButton
          ariaLabel="Show / Hide Data Sources"
          iconColor={propertyIsExpanded ? 'buttonActive' : 'buttonDefault'}
          icon={propertyIsExpanded ? 'database-filled' : 'database'}
          size="xs"
          variant="tertiary"
          onClick={() => {
            dispatch(setFeatureGridExpandedPropertyId(propertyIsExpanded ? undefined : propertyId));
          }}
        />
      )}
    </Flex>
  );
};
